/**
 * LoadingSpinner Component - CSS-First Design Token Architecture
 *
 * Accessible loading indicator with multiple sizes and variants.
 * Respects prefers-reduced-motion for accessibility.
 *
 * WCAG 2.1 AA compliant.
 */

interface LoadingSpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Visual variant */
  variant?: 'primary' | 'secondary' | 'inverse'
  /** Custom label for screen readers */
  label?: string
  /** Show label visually */
  showLabel?: boolean
  /** Center in container */
  centered?: boolean
}

export function LoadingSpinner({
  size = 'md',
  variant = 'primary',
  label = 'Loading',
  showLabel = false,
  centered = false,
}: LoadingSpinnerProps) {
  const spinnerClasses = [
    'loading-spinner',
    `loading-spinner--${size}`,
    `loading-spinner--${variant}`,
    centered ? 'loading-spinner--centered' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={spinnerClasses} role="status" aria-live="polite">
      <svg
        className="loading-spinner__icon"
        viewBox="0 0 50 50"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          className="loading-spinner__track"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="4"
        />
        <circle
          className="loading-spinner__spinner"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
      <span className={showLabel ? 'loading-spinner__label' : 'loading-spinner__label--sr-only'}>
        {label}
      </span>
    </div>
  )
}

LoadingSpinner.displayName = 'LoadingSpinner'
